import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, UserX, Search } from "lucide-react";

const ROLES = ["OT Head", "Head OT Technician", "Senior OT Technician", "OT Technician", "Nursing Staff", "Junior OT Technician", "Fresher / Trainee", "Other"];
const STATUS = ["Active", "On Leave", "Inactive"];
const statusColor = (s) => s === "Active" ? "bg-emerald-600" : s === "On Leave" ? "bg-[#C9A24B] text-[#0b1120]" : "bg-slate-600";

export default function MasterList() {
  const { branches, loadReference } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => { setLoading(true); api.get("/technicians").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const blank = { name: "", google_email: "", role: "OT Technician", branch: branches[0] || "Sarita Vihar", specialization: "", joining_date: "", status: "Active", contact_number: "", remarks: "" };
  const openNew = () => { setEdit({ ...blank }); setOpen(true); };
  const openEdit = (r) => { setEdit({ ...r }); setOpen(true); };

  const save = async () => {
    if (!edit.name || !edit.google_email) { toast.error("Name & email required"); return; }
    try {
      if (edit.employee_id) await api.put(`/technicians/${edit.employee_id}`, edit);
      else await api.post("/technicians", edit);
      toast.success("Technician saved — now available across all modules");
      setOpen(false); load(); loadReference();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deactivate = async (r) => {
    await api.delete(`/technicians/${r.employee_id}`);
    toast.success("Deactivated (history preserved)"); load(); loadReference();
  };

  const filtered = rows.filter((r) => `${r.name} ${r.employee_id} ${r.branch} ${r.role}`.toLowerCase().includes(q.toLowerCase()));
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  return (
    <div className="animate-fade-up" data-testid="master-list-page">
      <PageHeader title="OT Technician Master List" subtitle="Standalone & manual. Add a technician once — they instantly appear in Attendance, Tracker, Scorecard & Leaderboard."
        action={<button data-testid="add-technician-btn" onClick={openNew} className="flex items-center gap-2 bg-[#C9A24B] text-[#0b1120] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all"><Plus size={16} /> Add Technician</button>} />

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input data-testid="search-technician" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, branch…" className={inp + " pl-9"} />
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <Empty text="Loading…" /> : filtered.length === 0 ? <Empty text="No technicians found." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                  {["Emp ID", "Name", "Email", "Role", "Branch", "Specialization", "Joined", "Status", "Contact", ""].map((h) => <th key={h} className="py-3 px-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.employee_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`tech-row-${r.employee_id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs text-[#C9A24B]">{r.employee_id}</td>
                    <td className="px-3 font-medium">{r.name}</td>
                    <td className="px-3 text-slate-400 text-xs">{r.google_email}</td>
                    <td className="px-3 text-slate-300 text-xs">{r.role}</td>
                    <td className="px-3 text-slate-400">{r.branch}</td>
                    <td className="px-3 text-slate-400 text-xs">{r.specialization}</td>
                    <td className="px-3 text-slate-500 text-xs">{r.joining_date}</td>
                    <td className="px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full text-white ${statusColor(r.status)}`}>{r.status}</span></td>
                    <td className="px-3 text-slate-400 text-xs">{r.contact_number}</td>
                    <td className="px-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-[#C9A24B]" data-testid={`edit-tech-${r.employee_id}`}><Pencil size={15} /></button>
                        {r.status !== "Inactive" && <button onClick={() => deactivate(r)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`deactivate-tech-${r.employee_id}`}><UserX size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{edit?.employee_id ? `Edit ${edit.employee_id}` : "Add Technician"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400">Name</label><input data-testid="tech-name" className={inp} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Google Email</label><input data-testid="tech-email" className={inp} value={edit.google_email} onChange={(e) => setEdit({ ...edit, google_email: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Role</label><select className={inp} value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>{ROLES.map((o) => <option key={o}>{o}</option>)}</select></div>
              <div><label className="text-xs text-slate-400">Branch</label><select className={inp} value={edit.branch} onChange={(e) => setEdit({ ...edit, branch: e.target.value })}>{branches.map((o) => <option key={o}>{o}</option>)}</select></div>
              <div><label className="text-xs text-slate-400">Specialization</label><input className={inp} value={edit.specialization} onChange={(e) => setEdit({ ...edit, specialization: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Joining Date</label><input type="date" className={inp} value={edit.joining_date} onChange={(e) => setEdit({ ...edit, joining_date: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Status</label><select className={inp} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{STATUS.map((o) => <option key={o}>{o}</option>)}</select></div>
              <div><label className="text-xs text-slate-400">Contact Number</label><input className={inp} value={edit.contact_number} onChange={(e) => setEdit({ ...edit, contact_number: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Remarks</label><input className={inp} value={edit.remarks} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} /></div>
              <button data-testid="save-technician-btn" onClick={save} className="col-span-2 bg-emerald-600 text-white font-semibold py-2 rounded-md hover:brightness-110 transition-all mt-1">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
